/**
 * 单 token 启发式 + diff token 类型分类。
 *
 * 这层为骨架对齐补两件事：
 * 1. 单 token keyword（`cost=237ms`、`pid=12345` 这种没空格的）骨架不参与，
 *    在内部识别 5 类常见变量段并替换成对应通配符，编译为 regex 后 .test()。
 * 2. 多 token 骨架命中后，对每个 diff token 跑 `classifyToken` 打类型标签
 *    （number / hex / quoted / activity / dotted / mixed / any），UI 据此
 *    在 tooltip 里显示「数字变量」「包名」等更具体的标签。
 *
 * 同构层（纯 TS、零 Node 依赖），前端 SPA 与 Node CLI/server 共用一份逻辑。
 */

import { escapeRegex } from './params';

/** 5 类启发式可识别的段类型。 */
export type SegmentKind = 'quoted' | 'activity' | 'dotted' | 'hex' | 'number';

/** 给 diff token 打的类型标签：5 类 + 部分覆盖/多段 + 完全识别不到。 */
export type DiffKind = SegmentKind | 'mixed' | 'any';

export interface VariableSegment {
  /** 段在 literal 中的起点（含）。 */
  start: number;
  /** 段在 literal 中的终点（不含）。 */
  end: number;
  kind: SegmentKind;
  /** 该段在原文中的字面文本。 */
  text: string;
  /** 替换为正则时使用的 pattern（如 `\d+`、`[\w.]+`）。 */
  pattern: string;
}

interface KindSpec {
  kind: SegmentKind;
  /** 全局扫描用的 regex；必须带 `g` 标志。 */
  regex: RegExp;
  /** 由 match[] 派生 pattern；可拒绝 match（返回 null）。 */
  toPattern: (m: RegExpExecArray) => string | null;
}

/**
 * 检测顺序（重叠后者丢弃）：
 *   quoted → activity → dotted → hex → number
 *
 * 之所以这样排：
 * - quoted 最高优先：避免引号内的数字被误识别成 number。
 * - activity（含 `/`）比 dotted（纯包名）更具体，先认 activity 再让 dotted
 *   去吃剩下的"裸包名"。
 * - hex 在 number 之前：`a3f12c` 优先 hex；`0x123` 也走 hex 不走 number。
 * - number 兜底。
 */
const KIND_SPECS: KindSpec[] = [
  {
    kind: 'quoted',
    // 单引号 / 双引号 各自一份；'g' 全局扫
    regex: /'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"/g,
    toPattern: (m) => {
      const head = m[0][0];
      return head === '"' ? `"[^"]*"` : `'[^']*'`;
    },
  },
  {
    kind: 'activity',
    // 形如 com.foo/.MainActivity 或 com.foo.bar/com.foo.SomeService
    regex: /\b[a-z][\w.]*\/[\w.$]+\b/g,
    toPattern: () => `\\S+/\\S+`,
  },
  {
    kind: 'dotted',
    // 保守包名：≥3 段、首段首字符小写
    regex: /\b[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*){2,}\b/g,
    toPattern: () => `[\\w.]+`,
  },
  {
    kind: 'hex',
    // 0x 前缀走宽松；裸 hex 要 ≥6 位、且至少含一个 a-f 字母（否则等同纯数字）
    regex: /0x[0-9a-fA-F]+|\b[0-9a-fA-F]{6,}\b/g,
    toPattern: (m) => {
      const text = m[0];
      if (text.startsWith('0x') || text.startsWith('0X')) return `[0-9a-fA-F]+`;
      // 必须包含至少一个 a-f 字母，纯数字交给 number 处理
      if (!/[a-fA-F]/.test(text)) return null;
      return `[0-9a-fA-F]+`;
    },
  },
  {
    kind: 'number',
    // 不加 \b：`cost=237ms` 这种数字紧贴字母的场景下 `\b\d+\b` 失效。
    // 已被 hex / 0x... 认领的数字段会被重叠丢弃过滤掉。
    regex: /\d+/g,
    toPattern: () => `\\d+`,
  },
];

/**
 * 扫一段字面文本，识别出所有可被替换为通配符的段。
 *
 * - 检测顺序见 `KIND_SPECS`。
 * - 已认领的字符区间 `[start, end)` 不会被后续 spec 重复认领（重叠丢弃）。
 * - 返回的段按 `start` 升序，且互不重叠。
 */
export function detectVariableSegments(literal: string): VariableSegment[] {
  const claimed: Array<{ start: number; end: number }> = [];
  const segments: VariableSegment[] = [];

  for (const spec of KIND_SPECS) {
    const re = new RegExp(spec.regex.source, spec.regex.flags);
    let m: RegExpExecArray | null;
    while ((m = re.exec(literal)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      // 重叠丢弃
      const overlapped = claimed.some(
        (c) => start < c.end && end > c.start,
      );
      if (overlapped) {
        // 防止零宽匹配死循环（虽然这些 spec 都不会零宽，仍兜底）
        if (m[0].length === 0) re.lastIndex++;
        continue;
      }
      const pattern = spec.toPattern(m);
      if (pattern === null) {
        if (m[0].length === 0) re.lastIndex++;
        continue;
      }
      claimed.push({ start, end });
      segments.push({ start, end, kind: spec.kind, text: m[0], pattern });
      if (m[0].length === 0) re.lastIndex++;
    }
  }

  segments.sort((a, b) => a.start - b.start);
  return segments;
}

/**
 * 把字面文本展开成正则 pattern：
 * - 固定段（segments 之间的字符）走 `escapeRegex`；
 * - 变量段直接拼对应通配符。
 *
 * `segs` 不传时内部会先 `detectVariableSegments`。
 *
 * 当 segs 为空 → 等同 `escapeRegex(literal)`，调用方应判断"零增益"
 * 短路（避免编译 regex 跑无意义的 .test()）。
 */
export function buildFuzzyPattern(
  literal: string,
  segs?: VariableSegment[],
): string {
  const segments = segs ?? detectVariableSegments(literal);
  if (segments.length === 0) return escapeRegex(literal);

  let out = '';
  let cursor = 0;
  for (const seg of segments) {
    if (seg.start > cursor) {
      out += escapeRegex(literal.slice(cursor, seg.start));
    }
    out += seg.pattern;
    cursor = seg.end;
  }
  if (cursor < literal.length) {
    out += escapeRegex(literal.slice(cursor));
  }
  return out;
}

/**
 * 给一个 token 打类型标签。
 *
 * - 0 段 → 'any'（5 类启发都不识别，留给 UI 兜底显示「任意 token」）
 * - 1 段且段覆盖整个 token → 对应 kind
 * - 1 段但只覆盖一部分（前后还有固定字符）→ 'mixed'
 * - 多段 → 'mixed'
 */
export function classifyToken(text: string): DiffKind {
  const segs = detectVariableSegments(text);
  if (segs.length === 0) return 'any';
  if (segs.length === 1) {
    const seg = segs[0];
    return seg.start === 0 && seg.end === text.length ? seg.kind : 'mixed';
  }
  return 'mixed';
}

export { escapeRegex };
